import React, { useState } from 'react';
import { Box, Button, Typography, IconButton, InputBase, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import LockIcon from '@material-ui/icons/Lock';
import PersonIcon from '@material-ui/icons/Person';
import BusinessIcon from '@material-ui/icons/Business';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles(() => ({
  root: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(150deg, #eff6ff 0%, #e0e7ff 50%, #f0fdf4 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  card: {
    width: 420,
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(59,130,246,0.12)',
    borderRadius: 20,
    padding: '32px 36px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 16px 48px rgba(59,130,246,0.12), 0 4px 16px rgba(0,0,0,0.06)',
  },
  logoWrap: {
    width: 56,
    height: 56,
    background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(16,185,129,0.35)',
  },
  title: { fontWeight: 800, fontSize: '1.35rem', color: '#0f172a', textAlign: 'center', lineHeight: 1.2 },
  subtitle: { fontSize: '0.72rem', color: '#64748b', textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: -12 },
  form: { width: '100%', display: 'flex', flexDirection: 'column', gap: 12 },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: 4, width: '100%' },
  fieldLabel: { fontSize: '0.68rem', fontWeight: 600, color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase' },
  fieldBox: {
    display: 'flex', alignItems: 'center', gap: 8, background: '#ffffff',
    border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 12px', height: 42,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:focus-within': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59,130,246,0.12)' },
    '&:hover': { borderColor: '#94a3b8' },
  },
  fieldIcon: { color: '#94a3b8', fontSize: 16, flexShrink: 0 },
  fieldInput: { flex: 1, fontSize: '0.85rem', color: '#0f172a', '& input': { padding: 0 } },
  row: { display: 'flex', gap: 10 },
  registerBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)', color: '#fff',
    fontWeight: 700, fontSize: '0.85rem', padding: '10px 0', borderRadius: 10,
    boxShadow: '0 4px 16px rgba(16,185,129,0.35)', marginTop: 4,
    '&:hover': { background: 'linear-gradient(135deg, #34d399 0%, #60a5fa 100%)', boxShadow: '0 6px 20px rgba(16,185,129,0.5)' },
    transition: 'all 0.2s ease',
  },
  error: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 8, padding: '8px 12px', color: '#dc2626', fontSize: '0.72rem', textAlign: 'center', width: '100%',
  },
  footer: { fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center' },
  backBtn: {
    position: 'absolute', top: 14, left: 14,
    color: '#64748b',
    '&:hover': { color: '#3b82f6', background: 'rgba(59,130,246,0.08)' },
  },
  loginLink: {
    fontSize: '0.78rem', color: '#64748b', textAlign: 'center', cursor: 'pointer',
    '&:hover': { color: '#3b82f6' },
  },
}));

function RegisterScreen({ onRegister, onBack }) {
  const classes = useStyles();
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!companyName.trim()) return setError('Nome da empresa é obrigatório');
    if (!name.trim()) return setError('Nome é obrigatório');
    if (!email.includes('@')) return setError('Email inválido');
    if (password.length < 8) return setError('Palavra-passe deve ter pelo menos 8 caracteres');
    if (password !== confirmPassword) return setError('As palavras-passe não coincidem');

    setLoading(true);
    const result = await onRegister({ companyName, name, email, password, phone });
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.card}>
        <IconButton className={classes.backBtn} size="small" onClick={onBack}>
          <ArrowBackIcon style={{ fontSize: 20 }} />
        </IconButton>
        <Box className={classes.logoWrap}>
          <DirectionsCarIcon style={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography className={classes.title}>Criar Conta</Typography>
        <Typography className={classes.subtitle}>Registo gratuito no Planeador de Rotas</Typography>
        <form className={classes.form} onSubmit={handleSubmit}>
          <Box className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel}>Nome da Empresa</Typography>
            <Box className={classes.fieldBox}>
              <BusinessIcon className={classes.fieldIcon} />
              <InputBase className={classes.fieldInput} placeholder="A sua empresa"
                value={companyName} onChange={e => setCompanyName(e.target.value)} autoFocus />
            </Box>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.fieldWrap} style={{ flex: 1 }}>
              <Typography className={classes.fieldLabel}>Nome</Typography>
              <Box className={classes.fieldBox}>
                <PersonIcon className={classes.fieldIcon} />
                <InputBase className={classes.fieldInput} placeholder="O seu nome"
                  value={name} onChange={e => setName(e.target.value)} />
              </Box>
            </Box>
            <Box className={classes.fieldWrap} style={{ flex: 1 }}>
              <Typography className={classes.fieldLabel}>Telefone (opcional)</Typography>
              <Box className={classes.fieldBox}>
                <PhoneIcon className={classes.fieldIcon} />
                <InputBase className={classes.fieldInput} placeholder="+351..."
                  value={phone} onChange={e => setPhone(e.target.value)} />
              </Box>
            </Box>
          </Box>
          <Box className={classes.fieldWrap}>
            <Typography className={classes.fieldLabel}>Email</Typography>
            <Box className={classes.fieldBox}>
              <EmailIcon className={classes.fieldIcon} />
              <InputBase className={classes.fieldInput} placeholder="email@empresa.com"
                value={email} onChange={e => { setEmail(e.target.value); setError(''); }} autoComplete="email" />
            </Box>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.fieldWrap} style={{ flex: 1 }}>
              <Typography className={classes.fieldLabel}>Palavra-passe</Typography>
              <Box className={classes.fieldBox}>
                <LockIcon className={classes.fieldIcon} />
                <InputBase className={classes.fieldInput} placeholder="Min. 8 caracteres"
                  type={showPass ? 'text' : 'password'} value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }} autoComplete="new-password" />
                <IconButton size="small" onClick={() => setShowPass(v => !v)} style={{ color: '#94a3b8', padding: 3 }}>
                  {showPass ? <VisibilityOffIcon style={{ fontSize: 14 }} /> : <VisibilityIcon style={{ fontSize: 14 }} />}
                </IconButton>
              </Box>
            </Box>
            <Box className={classes.fieldWrap} style={{ flex: 1 }}>
              <Typography className={classes.fieldLabel}>Confirmar</Typography>
              <Box className={classes.fieldBox}>
                <LockIcon className={classes.fieldIcon} />
                <InputBase className={classes.fieldInput} placeholder="Repetir"
                  type={showPass ? 'text' : 'password'} value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setError(''); }} autoComplete="new-password" />
              </Box>
            </Box>
          </Box>
          {error && <Box className={classes.error}>{error}</Box>}
          <Button type="submit" fullWidth variant="contained" className={classes.registerBtn} disableElevation disabled={loading}>
            {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Criar Conta'}
          </Button>
        </form>
        <Typography className={classes.loginLink} onClick={onBack}>
          Já tem conta? Entrar
        </Typography>
        <Typography className={classes.footer}>EcooGPS {new Date().getFullYear()} - Planeamento de rotas</Typography>
      </Box>
    </Box>
  );
}

export default RegisterScreen;
